import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  getGridBooleanOperators,
  GridFilterOperator,
  GridInitialState,
  GridSingleSelectColDef,
  GridValidRowModel,
  GridFilterModel
} from '@mui/x-data-grid-pro';
import { cloneDeep } from 'lodash';
import { User } from '../../../store/User';
import { getNestedValue } from '../../../utils/helpers';
import { Commission } from '../../../store/Commission';
import { Customer } from '../../../store/Customer';
import { Asset } from '../../../store/Asset';
import { ProvisionalAsset } from '../../../store/ProvisionalAsset';
import { LinkEvent } from '../../../store/LinkEvent';
import { ApMap } from '../../../store/ApMap';
import { InvalidLink } from '../../../store/Link';
import { TsdMap } from '../../../store/TsdMap';
import { SupplierMap } from '../../../store/SupplierMap';
import { Ticket } from '../../../store/Ticket';
import { applyFilter, DatePickerInput } from './DateFilterInput';
import { applyFilter as applyMonthFilter, MonthPickerInput } from './MonthFilterInput';
import { RpmAccount } from '../../../store/RpmAccount';
import { GridCustomColDef, GridSelectCustomColDef } from '../../types';
import { defaultSort, GridPreferences, PrefKey } from '../../../store/UserCustomizations';
import { ApiAdvancedFilter, FilterableField } from '../../../store/GeneralStore';
import { AutoCompleteFilter } from './AutoCompleteInput';
import { ProductMap } from '../../../store/ProductMap';
import { CustomerMap } from '../../../store/CustomerMap';

export const getValuesForColumn = (rows: GridValidRowModel[], field: string) => {
  return rows
    .reduce((acc, ele: GridValidRowModel) => {
      const val = field.includes('.') ? getNestedValue(ele, field) : ele[field];
      if (val && !acc.includes(val)) acc.push(val);
      return acc;
    }, [])
    .sort();
};

export const getGridColumns = (
  data:
    | User[]
    | Commission[]
    | Customer[]
    | Asset[]
    | ProvisionalAsset[]
    | LinkEvent[]
    | InvalidLink[]
    | RpmAccount[]
    | TsdMap[]
    | ApMap[]
    | Ticket[]
    | SupplierMap[]
    | ProductMap[]
    | CustomerMap[],
  defaultCols: GridSingleSelectColDef[],
  grid: PrefKey
): GridSingleSelectColDef[] => {
  return defaultCols.reduce((acc: GridSingleSelectColDef[], col: GridSelectCustomColDef) => {
    const filterValues = getValuesForColumn(data, col.field);
    if (Array.isArray(filterValues)) col.valueOptions = filterValues;
    acc.push(col);

    return acc;
  }, []);
};

export const getColsForTab = (defaultCols: GridCustomColDef[], disabledFilters: string[]) => {
  const columns = cloneDeep(defaultCols);
  columns.forEach((col: GridCustomColDef) => {
    col.filterable = disabledFilters.includes(col.field) ? false : col.filterable;
  });

  return columns;
};

type DateCompare = (cell: number, filter: number) => boolean;
const baseIsDateOption = getGridDateOperators().find((op) => op.value === 'is');

const buildOrEmpty = (label: string, value: string, compare: DateCompare): GridFilterOperator => ({
  label,
  value,
  getApplyFilterFn: (filterItem) => {
    if (!filterItem.value) return null;

    const filterDate = new Date(filterItem.value).getTime();
    return ({ value }) => {
      if (!value) return true;
      return compare(new Date(value).getTime(), filterDate);
    };
  },
  InputComponent: baseIsDateOption?.InputComponent,
  InputComponentProps: baseIsDateOption?.InputComponentProps
});

const customDateOperators = [
  buildOrEmpty('is before or empty', 'beforeOrEmpty', (cell, filter) => cell < filter),
  buildOrEmpty('is on or before or empty', 'onOrBeforeOrEmpty', (cell, filter) => cell <= filter),
  buildOrEmpty('is after or empty', 'afterOrEmpty', (cell, filter) => cell > filter),
  buildOrEmpty('is on or after or empty', 'onOrAfterOrEmpty', (cell, filter) => cell >= filter)
];

export const standardOperators = (): GridFilterOperator[] => {
  const containsOperator = getGridStringOperators().find((op) => op.value === 'contains') as GridFilterOperator;

  return containsOperator ? [containsOperator] : [];
};

export const datePickerOperator: GridFilterOperator = {
  label: 'is',
  value: 'is',
  getApplyFilterFn: applyFilter,
  InputComponent: DatePickerInput
};

export const getInitialState = (
  gridSettings: GridPreferences,
  columns: GridCustomColDef[],
  page: PrefKey
): GridInitialState => {
  const state: GridInitialState = JSON.parse(JSON.stringify(gridSettings?.muiConfig));

  columns.forEach((col) => {
    if (
      col.hideable === false &&
      col.pinned &&
      state &&
      state.columns?.columnVisibilityModel &&
      state.pinnedColumns &&
      state.columns.orderedFields
    ) {
      state.columns.columnVisibilityModel[col.field] = true;
      if (state.pinnedColumns[col.pinned] && !(state.pinnedColumns[col.pinned] || []).includes(col.field)) {
        state.pinnedColumns[col.pinned] = [col.field, ...(state.pinnedColumns[col.pinned] || [])];
      }
      if (!state.columns.orderedFields.includes(col.field)) {
        state.columns.orderedFields.unshift(col.field);
      }
    }
    if (
      col.hideByDefault &&
      !gridSettings?.muiConfig?.columns?.columnVisibilityModel?.[col.field] &&
      state?.columns?.columnVisibilityModel
    ) {
      state.columns.columnVisibilityModel[col.field] = false;
    }
  });

  if (state.sorting && !state.sorting.sortModel?.length) {
    // Grids without an entry in defaultSort would otherwise get undefined in a field typed GridSortModel,
    // which callers then forward as an explicit `orders: undefined`.
    state.sorting.sortModel = defaultSort[page] ?? [];
  }

  return state;
};

const allowedDedupOperators = ['doesNotEqual', 'before', 'after', 'onOrBefore', 'onOrAfter', 'isEmpty', 'isNotEmpty'];

export const mergeAndDeduplicateFilters = (
  array1: FilterableField[],
  array2: FilterableField[],
  applied = false
): FilterableField[] => {
  const prefix = applied ? 'applied_' : '';
  const prefixedArray2 = array2.map((item) => ({ ...item, field: `${prefix}${item.field}` }));
  const merged = [...prefixedArray2, ...array1];
  const uniqueFields = new Set<string>();
  const deduplicated: FilterableField[] = [];

  for (const item of merged) {
    if (!uniqueFields.has(item.field) || allowedDedupOperators.includes(item.operator)) {
      uniqueFields.add(item.field);
      deduplicated.push(item);
    }
  }

  return deduplicated;
};

export const advancedDatePickerOperator = (): GridFilterOperator[] => {
  return [...getGridDateOperators(), ...customDateOperators];
};

export const advancedOperators = (): GridFilterOperator[] => {
  return getGridStringOperators();
};

export const advancedNumericOperators = (): GridFilterOperator[] => {
  return getGridNumericOperators();
};

export const advancedCountOperators = (): GridFilterOperator[] =>
  getGridNumericOperators().filter(({ value }) => !['isEmpty', 'isNotEmpty'].includes(value));

export const advancedBooleanOperators = (): GridFilterOperator[] => getGridBooleanOperators();

export const runPickerOperators = (): GridFilterOperator[] => {
  return getGridDateOperators().map((op) => ({
    value: op.value,
    label: op.value,
    getApplyFilterFn: applyMonthFilter,
    InputComponent: ['isEmpty', 'isNotEmpty'].includes(op.value) ? undefined : MonthPickerInput
  }));
};

export const advancedEnumOperators = (withEmptyOperators = false): GridFilterOperator[] => {
  const allOps = advancedOperators();
  let operators = ['isAnyOf', 'equals', 'doesNotEqual'];
  if (withEmptyOperators) {
    operators = [...operators, 'isEmpty', 'isNotEmpty'];
  }
  return operators.map((op) => {
    return {
      ...allOps.find((o) => o.value === op),
      InputComponent: ['isEmpty', 'isNotEmpty'].includes(op) ? undefined : AutoCompleteFilter
    };
  }) as GridFilterOperator[];
};

export const buildAdvancedFilterSet = (filters: GridFilterModel, filterKeys?: string[]) => {
  const advancedFilterSet = (filters.items || []).reduce<ApiAdvancedFilter>(
    (filterSet, item) => {
      if (
        (filterKeys?.includes(item.field) && ['isEmpty', 'isNotEmpty'].includes(item.operator)) ||
        (item.value !== undefined && item.value !== null && item.value !== '')
      ) {
        filterSet.filters?.push({
          field: item.field,
          operator: item.operator,
          value: item.value
        });
      }

      return filterSet;
    },
    { filters: [] } as ApiAdvancedFilter
  );
  advancedFilterSet.logicalOperator = filters?.logicOperator || 'and';

  return advancedFilterSet;
};
